import { useId } from 'react';
import { flattenClassNames } from '@trukt/portal-utils/flattenClassNames';
import { useTheme } from '../../../hooks/useTheme';
import { Typography } from '../../Typography';

import { CheckboxProps } from './Checkbox.types';

export type { CheckboxProps };

export const Checkbox = ({
  testId = 'input__wrapper',
  testIdInput = 'input',
  backgroundColor,
  showBackgroundColorWhenDisabled = false,
  className = '',
  label,
  labelPosition = 'right',
  secondaryLabel = '',
  value = false,
  checkColor,
  border,
  onClick = () => {},
  onChange = () => {},
  disabled = false,
  ...rest
}: CheckboxProps) => {
  const id = useId();
  const theme = useTheme();
  return (
    <div
      data-testid={testId}
      className={flattenClassNames([
        'flex flex-col justify-center',
        disabled ? 'none' : undefined,
        className,
      ])}
      // since the width/height of the input is 0. This will actually fire the parents
      // event listener as well if it has one. We can stop that effect by stopping propagation
      onClick={(e) => e.stopPropagation()}
    >
      {secondaryLabel && (
        <div className="mb-1 flex items-center">
          <div className="flex items-center gap-[0.1rem]">
            <Typography htmlFor={id} data-testid="input__label">
              {secondaryLabel}
            </Typography>
          </div>
        </div>
      )}

      <div className="flex items-center">
        {label && labelPosition === 'left' && (
          <Typography htmlFor={id} className="mr-2" data-testid="checkbox__label__left">
            {label}
          </Typography>
        )}
        <div className="h-6 w-6">
          <label
            className={flattenClassNames([
              'flex h-full w-full items-center justify-center',
              !disabled ? 'cursor-pointer' : 'cursor-default',
            ])}
          >
            <input
              id={id}
              data-testid={testIdInput}
              className="hidden h-0 w-0"
              disabled={disabled}
              type="checkbox"
              onClick={onClick}
              checked={value}
              onChange={onChange}
              {...rest}
            />
            <div
              data-testid="checkbox__content"
              className={flattenClassNames([
                'flex h-full w-full items-center justify-center rounded-md transition-all',
                border ?? 'border-2 border-solid border-secondary-200',
              ])}
              css={{
                backgroundColor:
                  disabled && !showBackgroundColorWhenDisabled
                    ? theme.palette.background.secondary
                    : (backgroundColor ?? theme.palette.common.white),
              }}
            >
              {value && (
                <span
                  className="h-3 w-3 rounded-[2.5px] bg-primary-100"
                  aria-checked={!!value}
                  aria-label="Checkbox selected"
                />
              )}
            </div>
          </label>
        </div>
        {label && labelPosition === 'right' && (
          <Typography htmlFor={id} className="ml-2" data-testid="checkbox__label__right">
            {label}
          </Typography>
        )}
      </div>
    </div>
  );
};
